import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { linkLandingUrl, reportNotFound, reportPageView, resolveLink } from "./api";

// Preview hosts and signed-in app areas are never counted.
const skipHost = () => {
  const h = window.location.hostname;
  return h === "localhost" || h.includes("lovable");
};
const PRIVATE = /^\/(admin|portal|dashboard|embed|auth)(\/|$)/;

/** Records one public page view per route change (feeds broken-link before/after). */
export function PageViewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (skipHost() || PRIVATE.test(pathname)) return;
    void reportPageView(pathname).catch(() => {});
  }, [pathname]);
  return null;
}

/**
 * 404 catcher for any page that turns out to be missing (catch-all route,
 * missing article, missing show): follows an admin redirect or logs the miss.
 */
export function useReportNotFound(active: boolean) {
  const { pathname, search } = useLocation();
  const slug = pathname.slice(1).toLowerCase();
  const isShortLink = /^[a-z0-9][a-z0-9-]{0,62}$/.test(slug);
  const routeKey = pathname + search;
  const [settledRoute, setSettledRoute] = useState<string | null>(null);
  useEffect(() => {
    if (!active || (!isShortLink && skipHost())) return;
    let alive = true;
    let redirecting = false;
    void (async () => {
      // podgo.io/<slug> is a short link when one exists (links.podgo.io may
      // forward here); that counts as a click, not a missed page.
      if (isShortLink) {
        const q = new URLSearchParams(search);
        const link = await resolveLink(slug, q.get("qr") === "1", q.get("c"));
        if (!alive) return;
        if (link?.destination) {
          redirecting = true;
          window.location.replace(linkLandingUrl(link.destination, slug, link.creative, link.variant));
          return;
        }
      }
      if (skipHost()) return;
      const r = await reportNotFound(pathname);
      if (alive && r.destination) {
        redirecting = true;
        window.location.replace(r.destination);
      }
    })().catch(() => {}).finally(() => {
      if (alive && !redirecting) setSettledRoute(routeKey);
    });
    return () => { alive = false; };
  }, [active, pathname, search, slug, isShortLink, routeKey]);
  return active && isShortLink && settledRoute !== routeKey;
}
